import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchMenu } from "../../actions/menuActions";
import { fetchRestaurantFooter } from "../../actions/footerActions";
import { useCart } from "../../components/CartContext";

import "./css/HomeScreen.css";


// ============================================================
// FALLBACK MENU IMAGE
// Used only when backend item has no image
// ============================================================

const fallbackMenuImage =
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=700&q=80";


// ============================================================
// STATIC HERO IMAGES
// These are NOT menu images.
// Menu images come from Django.
// ============================================================

const heroImages = [
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1000&q=85",
  "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1000&q=85",
  "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1000&q=85",
  "https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?auto=format&fit=crop&w=1000&q=85",
  "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1000&q=85",
];


// ============================================================
// CREATE SAFE SECTION ID
// ============================================================

function createSectionId(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}


// ============================================================
// MENU ITEM
// ============================================================

function MenuItem({ item, onAddToCart }) {
  const price = Number(item.price || 0);

  return (
    <article className="theme3-menu-card">

      <div className="theme3-menu-card-image-wrapper">

        <img
          src={item.image || fallbackMenuImage}
          alt={item.name}
          className="theme3-menu-card-image"
          loading="lazy"
        />

        <div className="theme3-menu-card-number">
          {String(item.display_order || "").padStart(2, "0")}
        </div>

      </div>


      <div className="theme3-menu-card-content">

        <div className="theme3-menu-card-top">

          <h3>
            {item.name}
          </h3>

          <span className="theme3-menu-card-price">
            ₹{price.toFixed(2)}
          </span>

        </div>


        {item.description && (
          <p className="theme3-menu-card-description">
            {item.description}
          </p>
        )}


        <div className="theme3-menu-card-bottom">

          <span className="theme3-menu-card-category">
            {item.category_name || "{restaurantName}"}
          </span>

          <button
            type="button"
            className="theme3-add-button"
            onClick={() => onAddToCart(item)}
          >
            ADD
            <span>+</span>
          </button>

        </div>

      </div>

    </article>
  );
}


// ============================================================
// AUTO SCROLL ROW
//
// Used for:
// 1. Signature dishes
// 2. ALL → every category
//
// Items are duplicated so the animation can loop continuously.
// ============================================================

function AutoScrollRow({
  items,
  onAddToCart,
  rowClassName = "",
}) {
  if (!items || items.length === 0) {
    return null;
  }

  const duplicatedItems = [...items, ...items];

  return (
    <div
      className={`theme3-auto-scroll ${rowClassName}`}
    >

      <div className="theme3-auto-scroll-track">

        {duplicatedItems.map((item, index) => (
          <div
            className="theme3-auto-scroll-item"
            key={`${item.id}-${index}`}
            aria-hidden={index >= items.length}
          >
            <MenuItem
              item={item}
              onAddToCart={onAddToCart}
            />
          </div>
        ))}

      </div>

    </div>
  );
}


// ============================================================
// SECTION TITLE
// ============================================================

function SectionTitle({
  eyebrow,
  title,
  description,
}) {
  return (
    <div className="theme3-section-heading">

      <div className="theme3-section-ornament">

        <span></span>

        <b>✦</b>

        <span></span>

      </div>

      {eyebrow && (
        <div className="theme3-section-eyebrow">
          {eyebrow}
        </div>
      )}

      <h2>
        {title}
      </h2>

      {description && (
        <p>
          {description}
        </p>
      )}

    </div>
  );
}


// ============================================================
// HOME SCREEN
// ============================================================

function HomeScreen() {

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    restaurantId,
    tableId,
  } = useParams();


  // ==========================================================
  // REDUX MENU
  // ==========================================================

  const {
    loading: menuLoading,
    categories = [],
    restaurantName: menuRestaurantName,
    error: menuError,
  } = useSelector(
    (state) => state.menu
  );


  // ==========================================================
  // REDUX THEME
  // ==========================================================

  const {
    restaurantName: themeRestaurantName,
  } = useSelector(
    (state) => state.theme
  );


  const footer = useSelector((state) => state.footer?.data);
  const restaurantName = footer?.restaurant_name || themeRestaurantName || menuRestaurantName || "Restaurant";


  // ==========================================================
  // CART
  // ==========================================================

  const {
    cart,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    cartCount,
    cartTotal,
  } = useCart();


  // ==========================================================
  // LOCAL STATE
  // ==========================================================

  const [cartOpen, setCartOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] =
    useState("ALL");


  // ==========================================================
  // FETCH MENU FROM BACKEND
  // ==========================================================

  useEffect(() => {

    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(restaurantId)
    );

    dispatch(fetchRestaurantFooter(restaurantId)).catch(() => {});

  }, [
    dispatch,
    restaurantId,
  ]);


  // ==========================================================
  // OPEN CART EVENT
  //
  // Theme 3 Header can dispatch:
  //
  // window.dispatchEvent(
  //   new Event("open-cart")
  // );
  // ==========================================================

  useEffect(() => {

    const handleOpenCart = () => {
      setCartOpen(true);
    };


    window.addEventListener(
      "open-cart",
      handleOpenCart
    );


    return () => {

      window.removeEventListener(
        "open-cart",
        handleOpenCart
      );

    };

  }, []);


  // ==========================================================
  // NORMALIZE BACKEND MENU
  //
  // Backend:
  //
  // categories
  //   ↓
  // category.items
  //
  // We sort using display_order.
  // ==========================================================

  const normalizedCategories = useMemo(() => {

    if (!Array.isArray(categories)) {
      return [];
    }


    return categories
      .filter(
        (category) =>
          category &&
          Array.isArray(category.items)
      )
      .map((category) => {

        const sortedItems = [
          ...category.items,
        ]
          .filter(
            (item) =>
              item &&
              item.is_available !== false
          )
          .sort(
            (a, b) =>
              Number(a.display_order || 0) -
              Number(b.display_order || 0)
          )
          .map((item) => ({
            ...item,

            category_id: category.id,

            category_name:
              category.name,

            category_description:
              category.description || "",
          }));


        return {
          ...category,

          items: sortedItems,
        };

      })
      .filter(
        (category) =>
          category.items.length > 0
      )
      .sort(
        (a, b) =>
          Number(a.display_order || 0) -
          Number(b.display_order || 0)
      );

  }, [categories]);


  // ==========================================================
  // SIGNATURE DISHES
  //
  // IMPORTANT:
  //
  // First item from EVERY category.
  //
  // Therefore:
  //
  // STARTERS #1
  // TANDOOR #1
  // CURRIES #1
  // BIRYANI #1
  // BREADS #1
  // DESSERTS #1
  // DRINKS #1
  //
  // No separate signature data is required.
  // ==========================================================

  const signatureDishes = useMemo(() => {

    return normalizedCategories
      .map(
        (category) =>
          category.items[0]
      )
      .filter(Boolean);

  }, [normalizedCategories]);


  // ==========================================================
  // ALL CATEGORY ITEMS
  // ==========================================================

  const allItems = useMemo(() => {

    return normalizedCategories.flatMap(
      (category) =>
        category.items
    );

  }, [normalizedCategories]);


  // ==========================================================
  // CURRENT CATEGORY
  // ==========================================================

  const currentCategory = useMemo(() => {

    if (
      selectedCategory === "ALL"
    ) {
      return null;
    }


    return normalizedCategories.find(
      (category) =>
        category.id === selectedCategory
    );

  }, [
    selectedCategory,
    normalizedCategories,
  ]);


  // ==========================================================
  // ADD TO CART
  // ==========================================================

  const handleAddToCart = (item) => {

    addToCart(item);

  };


  // ==========================================================
  // CHECKOUT
  // ==========================================================

  const handleCheckout = () => {

    if (!cart.length) {
      return;
    }


    navigate(
      `/${restaurantId}/table/${tableId}/checkout`
    );

  };


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <main className="theme3-home">


      {/* =====================================================
          HERO
      ====================================================== */}

      <section
        id="home"
        className="theme3-hero"
      >

        <div className="theme3-hero-overlay"></div>


        <div className="theme3-hero-content">

          <div className="theme3-hero-small">
            ✦ WELCOME TO {restaurantName.toUpperCase()} ✦
          </div>


          <h1>
            The Art of
            <span>
              Indian Cuisine
            </span>
          </h1>


          <p>
            A refined journey through
            authentic Indian flavours,
            traditional techniques and
            unforgettable dining.
          </p>


          <a
            href="#menu"
            className="theme3-hero-button"
          >
            EXPLORE OUR MENU
            <span>→</span>
          </a>

        </div>


        <div className="theme3-hero-gallery">

          {heroImages.map(
            (image, index) => (

              <div
                className={`theme3-hero-food theme3-hero-food-${index}`}
                key={image}
              >

                <img
                  src={image}
                  alt="Indian cuisine"
                />

              </div>

            )
          )}

        </div>


        <div className="theme3-hero-scroll">
          SCROLL TO DISCOVER
          <span>↓</span>
        </div>

      </section>


      {/* =====================================================
          CULINARY JOURNEY
      ====================================================== */}

      <section
        id="story"
        className="theme3-journey-section"
      >

        <div className="theme3-container">

          <SectionTitle
            eyebrow="OUR PHILOSOPHY"
            title="A Culinary Journey"
            description="Where tradition meets contemporary Indian dining."
          />


          <div className="theme3-journey-grid">

            <div className="theme3-journey-image">

              <img
                src={heroImages[3]}
                alt="Indian dining"
                loading="lazy"
              />

            </div>


            <div className="theme3-journey-content">

              <div className="theme3-gold-number">
                01
              </div>

              <h3>
                Rooted in Tradition
              </h3>

              <p>
                Our kitchen celebrates the rich
                culinary heritage of India while
                presenting every dish with a refined
                modern touch.
              </p>

              <p>
                From fragrant spices to slow-cooked
                curries and traditional tandoor
                cooking, every plate is prepared
                with attention to detail.
              </p>


              <div className="theme3-signature-line">
                <span></span>
                {restaurantName}
                <span></span>
              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          SIGNATURE DISHES
      ====================================================== */}

      <section
        className="theme3-signature-section"
      >

        <div className="theme3-container">

          <SectionTitle
            eyebrow="FROM OUR KITCHEN"
            title="Signature Dishes"
            description="The first signature dish from every menu category."
          />


          {signatureDishes.length > 0 ? (

            <AutoScrollRow
              items={signatureDishes}
              onAddToCart={handleAddToCart}
              rowClassName="theme3-signature-row"
            />

          ) : (

            <div className="theme3-empty-menu">
              Signature dishes will appear here.
            </div>

          )}

        </div>

      </section>


      {/* =====================================================
          MENU
      ====================================================== */}

      <section
        id="menu"
        className="theme3-menu-section"
      >

        <div className="theme3-container">

          <SectionTitle
            eyebrow="THE MENU"
            title="Discover Our Flavours"
            description="Choose a category or explore the complete menu."
          />


          {/* =================================================
              MENU CATEGORY TABS
          ================================================== */}

          <div className="theme3-menu-tabs">

            <button
              type="button"
              className={
                selectedCategory === "ALL"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setSelectedCategory("ALL")
              }
            >
              ALL
            </button>


            {normalizedCategories.map(
              (category) => (

                <button
                  type="button"
                  key={category.id}
                  className={
                    selectedCategory === category.id
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setSelectedCategory(category.id)
                  }
                >
                  {category.name}
                </button>

              )
            )}

          </div>


          {/* =================================================
              LOADING
          ================================================== */}

          {menuLoading && (

            <div className="theme3-menu-status">

              <div className="theme3-loader"></div>

              <p>
                Preparing the menu...
              </p>

            </div>

          )}


          {/* =================================================
              ERROR
          ================================================== */}

          {!menuLoading && menuError && (

            <div className="theme3-menu-status theme3-menu-error">

              <span>!</span>

              <p>
                {typeof menuError === "string"
                  ? menuError
                  : "Unable to load the menu."}
              </p>

            </div>

          )}


          {/* =================================================
              EMPTY
          ================================================== */}

          {!menuLoading &&
            !menuError &&
            normalizedCategories.length === 0 && (

              <div className="theme3-menu-status">

                <p>
                  No menu items are available.
                </p>

              </div>

            )}


          {/* =================================================
              ALL
              
              Every category:
              
              6 items
              horizontal auto-scroll
          ================================================== */}

          {!menuLoading &&
            !menuError &&
            selectedCategory === "ALL" &&
            normalizedCategories.length > 0 && (

              <div className="theme3-all-menu">

                {normalizedCategories.map(
                  (category) => (

                    <section
                      className="theme3-all-category"
                      key={category.id}
                    >

                      <div className="theme3-all-category-heading">

                        <div>

                          <span>
                            MENU CATEGORY
                          </span>

                          <h3>
                            {category.name}
                          </h3>

                        </div>


                        <strong>
                          {category.items.length}
                          {" "}
                          ITEMS
                        </strong>

                      </div>


                      {category.description && (

                        <p className="theme3-category-description">
                          {category.description}
                        </p>

                      )}


                      <AutoScrollRow
                        items={category.items}
                        onAddToCart={handleAddToCart}
                        rowClassName="theme3-category-row"
                      />

                    </section>

                  )
                )}

              </div>

            )}


          {/* =================================================
              INDIVIDUAL CATEGORY
              
              Normal responsive grid.
              NO AUTO SCROLL.
          ================================================== */}

          {!menuLoading &&
            !menuError &&
            currentCategory && (

              <section className="theme3-selected-category">

                <div className="theme3-selected-category-heading">

                  <div>

                    <span>
                      SELECTED CATEGORY
                    </span>

                    <h3>
                      {currentCategory.name}
                    </h3>

                  </div>


                  <strong>
                    {currentCategory.items.length}
                    {" "}
                    ITEMS
                  </strong>

                </div>


                {currentCategory.description && (

                  <p className="theme3-category-description">
                    {currentCategory.description}
                  </p>

                )}


                <div className="theme3-normal-menu-grid">

                  {currentCategory.items.map(
                    (item) => (

                      <MenuItem
                        key={item.id}
                        item={item}
                        onAddToCart={handleAddToCart}
                      />

                    )
                  )}

                </div>

              </section>

            )}

        </div>

      </section>


      {/* =====================================================
          STORY
      ====================================================== */}

      <section
        className="theme3-story-section"
      >

        <div className="theme3-container">

          <div className="theme3-story-grid">

            <div className="theme3-story-content">

              <div className="theme3-section-eyebrow">
                OUR STORY
              </div>

              <h2>
                Crafted With
                <span>
                  Passion
                </span>
              </h2>

              <p>
                Indian cuisine is a story of
                generations, regions, spices and
                traditions.
              </p>

              <p>
                At {restaurantName}, we bring these
                traditions together with a modern
                dining experience designed around
                flavour, hospitality and detail.
              </p>


              <div className="theme3-story-signature">
                <span>
                  ✦
                </span>

                AUTHENTIC
                <span>
                  ✦
                </span>

              </div>

            </div>


            <div className="theme3-story-image">

              <img
                src={heroImages[1]}
                alt="Indian cuisine"
                loading="lazy"
              />

              <div className="theme3-story-frame"></div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CHEF
      ====================================================== */}

      <section
        className="theme3-chef-section"
      >

        <div className="theme3-container">

          <div className="theme3-chef-card">

            <div className="theme3-chef-image">

              <img
                src={heroImages[4]}
                alt="Chef special"
                loading="lazy"
              />

            </div>


            <div className="theme3-chef-content">

              <div className="theme3-section-eyebrow">
                FROM THE CHEF
              </div>

              <h2>
                Every Plate
                <span>
                  Tells a Story
                </span>
              </h2>

              <p>
                We select each ingredient for its
                quality, freshness and ability to
                create a memorable flavour.
              </p>

              <div className="theme3-chef-line"></div>

              <strong>
                {restaurantName}
              </strong>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          GALLERY
      ====================================================== */}

      <section
        id="gallery"
        className="theme3-gallery-section"
      >

        <div className="theme3-container">

          <SectionTitle
            eyebrow="THE EXPERIENCE"
            title="Our Gallery"
            description="A glimpse into our food, atmosphere and dining experience."
          />


          <div className="theme3-gallery-grid">

            {[
              heroImages[0],
              heroImages[1],
              heroImages[2],
              heroImages[3],
              heroImages[4],
            ].map(
              (image, index) => (

                <div
                  className={`theme3-gallery-item theme3-gallery-item-${index}`}
                  key={image}
                >

                  <img
                    src={image}
                    alt={`${restaurantName} gallery ${index + 1}`}
                    loading="lazy"
                  />

                </div>

              )
            )}

          </div>

        </div>

      </section>

      


      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="theme3-final-section">

        <div className="theme3-final-overlay"></div>

        <div className="theme3-final-content">

          <div className="theme3-section-eyebrow">
            A TABLE AWAITS
          </div>

          <h2>
            Taste The
            <span>
              Difference
            </span>
          </h2>

          <p>
            Explore our menu and discover
            your next favourite dish.
          </p>


          <a
            href="#menu"
            className="theme3-final-button"
          >
            EXPLORE MENU
            <span>→</span>
          </a>

        </div>

      </section>


      {/* =====================================================
          FLOATING CART
      ====================================================== */}

      {cartCount > 0 && (

        <button
          type="button"
          className="theme3-floating-cart"
          onClick={() =>
            setCartOpen(true)
          }
        >

          <span className="theme3-floating-cart-icon">
            🛒
          </span>


          <span className="theme3-floating-cart-text">
            <small>
              YOUR ORDER
            </small>

            <strong>
              {cartCount} ITEM
              {cartCount !== 1 ? "S" : ""}
            </strong>
          </span>


          <span className="theme3-floating-cart-total">
            ₹{Number(cartTotal).toFixed(2)}
          </span>

        </button>

      )}


      {/* =====================================================
          CART DRAWER
      ====================================================== */}

      {cartOpen && (

        <div
          className="theme3-cart-overlay"
          onClick={() =>
            setCartOpen(false)
          }
        >

          <aside
            className="theme3-cart-drawer"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            <div className="theme3-cart-header">

              <div>

                <span>
                  {restaurantName}
                </span>

                <h2>
                  Your Order
                </h2>

                <p>
                  {cartCount} item
                  {cartCount !== 1 ? "s" : ""}
                </p>

              </div>


              <button
                type="button"
                className="theme3-cart-close"
                onClick={() =>
                  setCartOpen(false)
                }
              >
                ×
              </button>

            </div>


            {cart.length === 0 ? (

              <div className="theme3-cart-empty">

                <div>
                  ✦
                </div>

                <h3>
                  Your order is empty
                </h3>

                <p>
                  Add something delicious
                  from our menu.
                </p>

              </div>

            ) : (

              <div className="theme3-cart-items">

                {cart.map(
                  (item) => (

                    <div
                      className="theme3-cart-item"
                      key={item.id}
                    >

                      <img
                        src={
                          item.image ||
                          fallbackMenuImage
                        }
                        alt={item.name}
                      />


                      <div className="theme3-cart-item-content">

                        <h3>
                          {item.name}
                        </h3>

                        <span>
                          ₹
                          {Number(item.price || 0).toFixed(2)}
                        </span>


                        <div className="theme3-cart-item-controls">

                          <button
                            type="button"
                            onClick={() =>
                              decreaseQuantity(
                                item.id
                              )
                            }
                          >
                            −
                          </button>


                          <strong>
                            {item.quantity}
                          </strong>


                          <button
                            type="button"
                            onClick={() =>
                              increaseQuantity(
                                item.id
                              )
                            }
                          >
                            +
                          </button>


                          <button
                            type="button"
                            className="theme3-remove-item"
                            onClick={() =>
                              removeFromCart(
                                item.id
                              )
                            }
                          >
                            REMOVE
                          </button>

                        </div>

                      </div>

                    </div>

                  )
                )}

              </div>

            )}


            {cart.length > 0 && (

              <div className="theme3-cart-footer">

                <div className="theme3-cart-total">

                  <span>
                    TOTAL
                  </span>

                  <strong>
                    ₹{Number(cartTotal).toFixed(2)}
                  </strong>

                </div>


                <button
                  type="button"
                  className="theme3-cart-checkout"
                  onClick={handleCheckout}
                >
                  CONTINUE TO ORDER
                  <span>
                    →
                  </span>
                </button>

              </div>

            )}

          </aside>

        </div>

      )}

    </main>

  );
}


export default HomeScreen;